import config from "../config";

function resolveMarimoValidatorRunUrl() {
  const fallback =
    process.env.NODE_ENV === "production"
      ? "https://marimo.exskilence.com/v1/run"
      : "http://127.0.0.1:8010/v1/run";
  const base = (
    config.MARIMO_VALIDATOR_URL ||
    process.env.REACT_APP_MARIMO_VALIDATOR_URL ||
    fallback
  ).trim();
  if (!base) {
    throw new Error("REACT_APP_MARIMO_VALIDATOR_URL is not configured.");
  }
  if (base.endsWith("/v1/run")) {
    return base;
  }
  return `${base.replace(/\/$/, "")}/v1/run`;
}

/** Same concatenation as LMS validate_notebook_cells (prefix of code cells). */
export function buildNotebookRunScript(codeCells) {
  return (codeCells || [])
    .map((cell) => String(cell.source || ""))
    .filter((source) => source.trim())
    .join("\n\n");
}

const ASSIGNMENT_RE = /^\s*([A-Za-z_][A-Za-z0-9_]*)\s*=/;

function assignmentName(line) {
  const match = ASSIGNMENT_RE.exec(String(line || "").trim());
  return match ? match[1] : null;
}

function valueVariableNames(valueLines) {
  const names = new Set();
  (valueLines || []).forEach((raw) => {
    const name = assignmentName(raw);
    if (name) names.add(name);
  });
  return names;
}

function stripAssignmentsForNames(source, names) {
  if (!names || names.size === 0) {
    return String(source || "");
  }
  return String(source || "")
    .split("\n")
    .filter((line) => {
      const name = assignmentName(line);
      return !(name && names.has(name));
    })
    .join("\n");
}

function silentPrefixBlock(prefixSources) {
  const prefix = (prefixSources || [])
    .map((source) => String(source || "").trim())
    .filter(Boolean)
    .join("\n\n");
  if (!prefix) return "";
  const indented = prefix
    .split("\n")
    .map((line) => (line ? `    ${line}` : ""))
    .join("\n");
  return [
    "import io as _lms_io",
    "import sys as _lms_sys",
    "_lms_out = _lms_sys.stdout",
    "_lms_sys.stdout = _lms_io.StringIO()",
    "try:",
    indented,
    "finally:",
    "    _lms_sys.stdout = _lms_out",
  ].join("\n");
}

/** Inject test Value lines and drop conflicting assignments in the target cell. */
export function buildScriptForTestCase(prefixSources, targetSource, valueLines) {
  const parts = [];
  const silentPrefix = silentPrefixBlock(prefixSources);
  if (silentPrefix) parts.push(silentPrefix);
  const valueStrs = (valueLines || []).map((value) => String(value || "").trim()).filter(Boolean);
  const overrideNames = valueVariableNames(valueStrs);
  const body = stripAssignmentsForNames(targetSource || "", overrideNames).trim();
  parts.push(...valueStrs);
  if (body) parts.push(body);
  return parts.join("\n\n");
}

export const MARIMO_VALIDATOR_BATCH_SIZE = 32;

/** Normalize expected/actual like py-executor (trim, bool/int/float coercion). */
export function normalizeGraderText(text) {
  const raw = String(text ?? "").replace(/\r\n/g, "\n").trim();
  const lower = raw.toLowerCase();
  if (lower === "true") return "True";
  if (lower === "false") return "False";
  if (lower === "none" || lower === "null") return "None";
  if (/^-?\d+$/.test(raw)) return String(parseInt(raw, 10));
  if (/^-?\d*\.\d+$/.test(raw)) return String(parseFloat(raw));
  return raw;
}

async function postValidatorBatch(url, scripts) {
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ scripts }),
  });
  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new Error(body || `Marimo validator returned HTTP ${response.status}.`);
  }
  const payload = await response.json();
  const results = payload && Array.isArray(payload.results) ? payload.results : [];
  while (results.length < scripts.length) {
    results.push({ ok: false, output: "", error: "Validation failed.", outputs: [] });
  }
  return results.slice(0, scripts.length);
}

/** POST multiple scripts (py-executor-style: one run per test case, chunked batches). */
export async function runMarimoValidatorScripts(scripts) {
  const cleaned = (scripts || []).map((script) => String(script || "").trim()).filter(Boolean);
  if (!cleaned.length) {
    return [];
  }
  const url = resolveMarimoValidatorRunUrl();
  const allResults = [];
  try {
    for (let offset = 0; offset < cleaned.length; offset += MARIMO_VALIDATOR_BATCH_SIZE) {
      const chunk = cleaned.slice(offset, offset + MARIMO_VALIDATOR_BATCH_SIZE);
      const chunkResults = await postValidatorBatch(url, chunk);
      allResults.push(...chunkResults);
    }
  } catch (error) {
    throw new Error(
      error?.message ||
        `Could not reach marimo-validator at ${url}. Start Exskilence-marimo-validator (python run.py).`
    );
  }
  return allResults;
}

function textFromValidatorItem(item) {
  if (!item || typeof item !== "object") {
    return "";
  }
  const direct = String(item.output || "").trim();
  if (direct) {
    return direct;
  }
  const blocks = Array.isArray(item.outputs) ? item.outputs : [];
  return blocks
    .filter((block) => block && block.type === "text")
    .map((block) => block.text || "")
    .join("\n")
    .trim();
}

export function mapValidatorResult(item, cellId) {
  if (!item || typeof item !== "object") {
    return {
      cell_id: cellId,
      ok: false,
      output: "",
      error: "Validation failed.",
      outputs: [],
    };
  }
  const output = textFromValidatorItem(item);
  const ok = item.ok !== false;
  return {
    cell_id: cellId,
    ok,
    output,
    error: ok ? "" : String(item.error || output || "Validation failed."),
    outputs: Array.isArray(item.outputs) ? item.outputs : [],
  };
}

/** POST one script to Exskilence-marimo-validator (not the LMS API). */
export async function runMarimoValidatorScript(script) {
  const trimmed = String(script || "").trim();
  if (!trimmed) {
    return { ok: true, output: "", error: "", outputs: [] };
  }
  const url = resolveMarimoValidatorRunUrl();
  let response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ scripts: [trimmed] }),
    });
  } catch (error) {
    throw new Error(
      `Could not reach marimo-validator at ${url}. Start Exskilence-marimo-validator (python run.py).`
    );
  }
  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new Error(
      body || `Marimo validator returned HTTP ${response.status}.`
    );
  }
  const payload = await response.json();
  const results = payload && Array.isArray(payload.results) ? payload.results : [];
  return results[0] || { ok: false, output: "", error: "Validation failed.", outputs: [] };
}

export async function runNotebookCellsOnValidator(cellId, codeCells) {
  const script = buildNotebookRunScript(codeCells);
  const item = await runMarimoValidatorScript(script);
  return mapValidatorResult(item, cellId);
}
